import { useCallback, useRef } from "react";
import { useTolgee, useTranslate, UseTranslateResult } from "@tolgee/react";

type TranslateFn = UseTranslateResult["t"];

/**
 * `useTranslate()` returns a fresh `t` identity every render, so depending on it
 * makes a memo recompute unconditionally. Its output only changes with the active
 * language, so depend on the returned `language` instead of `t`:
 *
 * ```ts
 * const {t, language} = useStableTranslate();
 * const rows = useMemo(() => items.map((i) => t(i.labelKey)), [items, language]);
 * ```
 *
 * This sidesteps `react-hooks/exhaustive-deps`, which can't see that `language`
 * stands in for `t`. Use it only where the recompute cost is real.
 */
const useStableTranslate = () => {
  const { t } = useTranslate();
  const language = useTolgee(["language"]).getLanguage();

  const tRef = useRef(t);
  tRef.current = t;

  const stableT = useCallback(
    ((...args: Parameters<TranslateFn>) =>
      tRef.current(...args)) as TranslateFn,
    []
  );

  return { t: stableT, language };
};

export default useStableTranslate;
